import { A, G } from '@mobily/ts-belt';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { ChipField, required, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import { GetB2bEmployeesEndpoint } from '@Common/types/bo/b2b/employees';
import { MAX_RANGE_ITEMS } from '@Common/types/bo/utils/build_zod_bo_list_query';

import { FileInput } from '@sh4res/bo-common/components';
import {
  getNameMismatches,
  NameMismatch,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { ConsultationPeriodSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/ConsultationPeriodSection';
import { NameMismatchEmployeeSection } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/components/NameMismatchEmployeeSection';
import { pickPreferredEmployeeByIdentifier } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/pickPreferredEmployeeByIdentifier';
import { AllocationsFileError } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/utils';
import {
  hasAnyVacationDays,
  parseFileVacationDays,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysFile';
import { DownloadAllocationSampleOptions } from '@sh4res/bo-common/components/B2bCampaigns/steps/components/DownloadAllocationSampleOptions/DownloadAllocationSampleOptions';
import { B2bCampaignFormValues } from '@sh4res/bo-common/components/B2bCampaigns/types';
import { useResourceBasePath, useWatchObject } from '@sh4res/bo-common/hooks';

type Employee = GetB2bEmployeesEndpoint['Client']['ResponseBody'][number];

const testId = 'BoExternalB2bCampaignCreateVacationDaysSetupSection';

type Props = {
  isEditionDisabled: boolean;
  isConsultationPeriodEditionDisabled: boolean;
  isStartDateEditionDisabled: boolean;
};

export const VacationDaysSetupSection = (props: Props) => {
  const { isEditionDisabled, isConsultationPeriodEditionDisabled, isStartDateEditionDisabled } =
    props;
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();
  const { company_id: companyId, b2b_contract_ids: contractIds } =
    useWatchObject<B2bCampaignFormValues>()(['company_id', 'b2b_contract_ids']);
  const [nameMismatches, setNameMismatches] = useState<NameMismatch[]>([]);

  const onFileChange = async (file: File | null) => {
    if (!file) {
      setValue('allocations', null);
      setNameMismatches([]);
      return;
    }
    try {
      const data = await parseFileVacationDays({ file });

      const { data: employees } = await dataProvider.getList<Employee>(
        `${resourceBasePath}employees`,
        {
          filter: { company_id: companyId, contract_ids: contractIds },
          sort: { field: 'created_at', order: 'DESC' },
          pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
        },
      );

      // When there are multiple employees with the same NIR/NTT, prefer the one with status "current"
      const preferredEmployees = pickPreferredEmployeeByIdentifier(employees);
      const employeeByIdentifier: Record<string, Employee | undefined> =
        Object.fromEntries(preferredEmployees);

      setNameMismatches(getNameMismatches(data, employeeByIdentifier));

      const enriched = data.map((alloc) => {
        const identifier = (alloc.nir ?? alloc.ntt) as string;
        const employee = employeeByIdentifier[identifier];
        return {
          ...alloc,
          identifier,
          registration_number: employee?.registration_number,
          employee_id: employee?.id,
          employee_exists: G.isNotNullable(employee),
          employee_fully_onboarded: employee?.approbation_state === 'finished',
          is_campaigns_blocked: employee?.is_campaigns_blocked ?? false,
          is_campaigns_blocked_regulatory: employee?.is_campaigns_blocked_regulatory ?? false,
          is_user_closed_or_archived: employee?.is_user_closed_or_archived ?? false,
        };
      });

      const filteredAllocations = enriched.filter(
        (entry) =>
          entry.employee_exists && !entry.is_user_closed_or_archived && hasAnyVacationDays(entry),
      );

      setValue('allocations', filteredAllocations, { shouldDirty: true });
    } catch (err) {
      const allocationsFileError = err as AllocationsFileError | Error;
      // eslint-disable-next-line no-console
      console.error(allocationsFileError);
      notify(
        allocationsFileError instanceof AllocationsFileError
          ? allocationsFileError.messageKey
          : 'resources.bo_common.b2b.campaigns.sections.allocations.errors.parsing_file',
        {
          type: 'error',
          messageArgs:
            allocationsFileError instanceof AllocationsFileError
              ? allocationsFileError.messageData
              : undefined,
        },
      );
      setValue('allocations_file', undefined);
      setNameMismatches([]);
    }
  };

  return (
    <>
      <ConsultationPeriodSection
        isEditionDisabled={isConsultationPeriodEditionDisabled}
        isStartDateEditionDisabled={isStartDateEditionDisabled}
        hasDescription={false}
      />
      <Box>
        <Typography variant="h6" fontWeight={700}>
          {translate('resources.bo_common.b2b.campaigns.sections.recipients.title')}
        </Typography>

        <Typography sx={{ mb: 2 }}>
          {translate('resources.bo_common.b2b.campaigns.sections.recipients.description')}
        </Typography>

        <Typography sx={{ mb: 2 }}>
          {translate('resources.bo_common.b2b.campaigns.sections.recipients.affiliates_warning')}
        </Typography>

        <DownloadAllocationSampleOptions />
        {!isEditionDisabled && A.isNotEmpty(contractIds) && (
          <FileInput
            data-testid={`${testId}_AllocationsFileInput`}
            source="allocations_file"
            accept=".xlsx,.xlsm"
            onChange={onFileChange}
            validate={[required()]}
            label="resources.bo_common.b2b.campaigns.fields.allocations_file">
            <ChipField data-testid={`${testId}_AllocationsFileTitleField`} source="title" />
          </FileInput>
        )}
        <NameMismatchEmployeeSection nameMismatches={nameMismatches} />
      </Box>
    </>
  );
};
